import { Alert, Box, CircularProgress } from '@mui/material';
import { ContactAssociationStep } from 'ContactAssociationStep';
import React from 'react';
import RecentlyViewedSidebar from '../admin/RecentlyViewedSidebar';
import StyledSection from '../styles/StyledSection';
import { StyledCard, StyledCardContent } from '../styles/styles';
import { EntityManagementAllocationsSection } from './EntityManagementAllocationsSection';
import { EntityManagementContactsSection } from './EntityManagementContactsSection';
import { EntityManagementDetailsCard } from './EntityManagementDetailsCard';
import { EntityManagementHeader } from './EntityManagementHeader';
import { useEntityManagement } from './useEntityManagement';

export const EntityManagement: React.FC = () => {
  const {
    state,
    handleEditToggle,
    handleSaveChanges,
    handleInputChange,
    handleForeignInvestorToggle,
    handleManageContacts,
    handleContactAssociationComplete,
    handlePromoteToOwner,
    toggleSection,
  } = useEntityManagement();

  if (state.loading && !state.entity) {
    return (
      <StyledSection tone="default">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </StyledSection>
    );
  }

  if (state.error && !state.entity) {
    return (
      <StyledSection tone="default">
        <Alert severity="error">{state.error}</Alert>
      </StyledSection>
    );
  }

  if (!state.entity) {
    return (
      <StyledSection tone="default">
        <Alert severity="error">Entity not found</Alert>
      </StyledSection>
    );
  }

  return (
    <StyledSection tone="default" noSidePaddingFrom="xs">
      <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: 4 }}>
        <Box sx={{ mb: 2 }}>
          <RecentlyViewedSidebar modelType="INVESTMENT_ENTITY" limit={5} compact={true} />
        </Box>

        <EntityManagementHeader
          entity={state.entity}
          isEditing={state.isEditing}
          editedName={state.editedEntity.name}
          onEditToggle={handleEditToggle}
          onSaveChanges={handleSaveChanges}
        />

        {state.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {state.error}
          </Alert>
        )}

        <EntityManagementDetailsCard
          entity={state.entity}
          isEditing={state.isEditing}
          editedEntity={state.editedEntity}
          onInputChange={handleInputChange}
          onForeignInvestorChange={handleForeignInvestorToggle}
        />

        <EntityManagementContactsSection
          contacts={state.contacts}
          expanded={state.expandedSections.contacts}
          showContactAssociation={state.showContactAssociation}
          onToggleExpanded={() => toggleSection('contacts')}
          onManageContacts={handleManageContacts}
          onPromoteToOwner={handlePromoteToOwner}
        />

        <EntityManagementAllocationsSection
          allocations={state.allocations}
          expanded={state.expandedSections.allocations}
          onToggleExpanded={() => toggleSection('allocations')}
        />

        {state.showContactAssociation && (
          <Box sx={{ mt: 4 }}>
            <StyledCard tone="default">
              <StyledCardContent>
                <ContactAssociationStep
                  entityId={state.entity.id}
                  entityName={state.entity.name}
                  entityType={state.entity.type}
                  onComplete={handleContactAssociationComplete}
                />
              </StyledCardContent>
            </StyledCard>
          </Box>
        )}
      </Box>
    </StyledSection>
  );
};
